import {
  Injectable,
  signal,
  computed,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY_PREFIX = 'app-theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Current user ID (set on login, cleared on logout) */
  private _userId: string | null = null;

  /** User-selected preference (light | dark | auto) */
  private _mode = signal<ThemeMode>('light');

  /** Resolved boolean — true when dark is actually applied */
  private _isDark = signal(false);

  /** System prefers dark */
  private _systemPrefersDark = signal(false);

  /**
   * Is the current page allowed to display dark mode?
   * false = public/onboarding pages → always light regardless of user pref.
   */
  private _darkAllowed = false;

  /** Public readonly signals */
  readonly mode = this._mode.asReadonly();
  readonly isDark = this._isDark.asReadonly();

  /** Computed label for display */
  readonly modeLabel = computed(() => {
    const labels: Record<ThemeMode, string> = {
      light: 'Clair',
      dark: 'Sombre',
      auto: 'Automatique',
    };
    return labels[this._mode()];
  });

  /** Computed icon for display */
  readonly modeIcon = computed(() => {
    const icons: Record<ThemeMode, string> = {
      light: '☀️',
      dark: '🌙',
      auto: '💻',
    };
    return icons[this._mode()];
  });

  private mediaQuery: MediaQueryList | null = null;

  constructor() {
    if (this.isBrowser) {
      if (typeof window.matchMedia === 'function') {
        this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this._systemPrefersDark.set(this.mediaQuery.matches);

        this.mediaQuery.addEventListener('change', (e: MediaQueryListEvent) => {
          this._systemPrefersDark.set(e.matches);
          if (this._mode() === 'auto') {
            this.apply();
          }
        });
      }

      // Restore user context from session if already logged in
      try {
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          const uid = user?._id || user?.id;
          if (uid) {
            this._userId = uid;
            this._darkAllowed = true; // user is logged in → allow dark on app pages
          }
        }
      } catch {
        /* ignore */
      }

      // Initialize from stored preference
      const stored = this.loadStoredMode();
      this._mode.set(stored);
      this.apply();
    }
  }

  // ── Public API ───────────────────────────────────────────────

  /** Set theme mode (called from settings UI) */
  setMode(mode: ThemeMode): void {
    this._mode.set(mode);
    this.persistMode(mode);
    this.apply();
  }

  /** Cycle through modes: light → dark → auto → light */
  toggleMode(): void {
    const cycle: ThemeMode[] = ['light', 'dark', 'auto'];
    const current = cycle.indexOf(this._mode());
    const next = cycle[(current + 1) % cycle.length];
    this.setMode(next);
  }

  /**
   * Called by app.component on NavigationEnd.
   * Sets whether the current page allows dark mode, then applies.
   */
  setDarkAllowed(allowed: boolean): void {
    this._darkAllowed = allowed;
    this.apply();
  }

  // ── Per-user session management ──────────────────────────────

  /** Call on login — loads this user's theme preference and applies. */
  onLogin(userId: string): void {
    this._userId = userId;
    this._darkAllowed = true; // user is navigating to an authenticated page
    const mode = this.loadStoredMode();
    this._mode.set(mode);
    this.apply();
  }

  /** Call on logout — resets to light and clears user context. */
  onLogout(): void {
    this._userId = null;
    this._darkAllowed = false;
    this._mode.set('light');
    this.apply();
  }

  // ── Private ──────────────────────────────────────────────────

  /**
   * Single source of truth: resolve the effective theme and apply it.
   * Rule: dark is applied ONLY if the page allows it AND the user wants it.
   */
  private apply(): void {
    let wantsDark = false;
    switch (this._mode()) {
      case 'dark':
        wantsDark = true;
        break;
      case 'auto':
        wantsDark = this._systemPrefersDark();
        break;
      // 'light' → wantsDark stays false
    }

    const effectiveDark = this._darkAllowed && wantsDark;
    this.applyDark(effectiveDark);
  }

  private applyDark(dark: boolean): void {
    this._isDark.set(dark);
    if (!this.isBrowser) return;

    const html = document.documentElement;
    if (dark) {
      html.classList.add('dark');
      html.style.colorScheme = 'dark';
    } else {
      html.classList.remove('dark');
      html.style.colorScheme = 'light';
    }
  }

  private get storageKey(): string {
    return this._userId
      ? `${STORAGE_KEY_PREFIX}-${this._userId}`
      : STORAGE_KEY_PREFIX;
  }

  private loadStoredMode(): ThemeMode {
    // Try per-user key first
    const key = this.storageKey;
    const stored = localStorage.getItem(key);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored;
    }
    // Fallback: legacy key (before per-user migration)
    if (this._userId) {
      const legacy = localStorage.getItem(STORAGE_KEY_PREFIX);
      if (legacy === 'light' || legacy === 'dark' || legacy === 'auto') {
        // Migrate: persist under per-user key and remove legacy
        localStorage.setItem(key, legacy);
        localStorage.removeItem(STORAGE_KEY_PREFIX);
        return legacy;
      }
    }
    return 'light';
  }

  private persistMode(mode: ThemeMode): void {
    if (!this.isBrowser) return;
    localStorage.setItem(this.storageKey, mode);
  }
}
